import _ from 'lodash';
import React, { useEffect, useState, useCallback } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { Box, Card as MuiCard, Divider, Grid as MuiGrid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CurrencyAmount } from '@cowbell/ui/core';
import withRouter from '../../../components/hocs/withRouter';
import CbButton from '../../../components/Buttons/CbButton';
import { QuoteStepsP250, QuoteStepsP100 } from './QuoteSteps';
import { QuoteStepLoader } from './QuoteStepLoader';

import {
  approveQuote,
  fetchQuoteDetails,
  fetchP250QuotePageDetails,
} from '../../../inbox/QuotesService';
import { ProductTypes } from '../../../types';
import { REDACTED_FIELD } from '../../../config/logrocket';

export const ApproveQuote = withRouter(({ history, location }: any) => {
  const classes = useStyles();
  // @ts-expect-error TS(2339): Property 'quoteId' does not exist on type '{}'.
  const { quoteId, product } = useParams();
  const [approving, setApproving] = useState(false);
  const {
    loading,
    data: { quote },
  } = useGetDependencies(quoteId, product);

  const handleApproval = useCallback(() => {
    setApproving(true);

    approveQuote({ quoteId, productType: product })
      .then(() => {
        setTimeout(() => {
          history.replace(`/customer/quotes/${quoteId}/attest/${product}`);
        }, 2000);
      })
      .catch(console.error.bind(console));
  }, [history, quoteId, product]);

  if (quote.customerStatus === 'APPROVED') {
    return <Redirect to={`/customer/quotes/${quoteId}/attest/${product}`} />;
  }

  if (loading) {
    return <QuoteStepLoader />;
  }

  return (
    <>
      {product === ProductTypes.p100 && (
        <QuoteStepsP100 location={location} agencyBilled={quote.agencyBilled} />
      )}
      {product === ProductTypes.p250 && (
        <QuoteStepsP250 location={location} agencyBilled={quote.agencyBilled} />
      )}

      <section>
        <MuiCard>
          <Box pl={3} pr={3} pb={3}>
            <MuiGrid container>
              <MuiGrid item md={6}>
                <h2>Approve Quote Number {quote.quoteNumber}</h2>
                <Divider
                  style={{
                    marginTop: '2.5rem',
                    marginBottom: '1.6666666667rem',
                  }}
                />
                <MuiGrid container spacing={10}>
                  <MuiGrid
                    item
                    md={6}
                    className={`${classes.details} ${classes.verticalDividingLine}`}
                    style={{ paddingTop: 0, paddingBottom: 24 }}
                  >
                    <h3 className={classes.headings}>QUOTE INFORMATION</h3>
                    <MuiGrid container>
                      <MuiGrid item md={6}>
                        Premium:
                      </MuiGrid>
                      <MuiGrid item md={6}>
                        <strong className="details-value">
                          <CurrencyAmount
                            amount={quote.totalPremium}
                            currency={quote.currency}
                          />
                        </strong>
                      </MuiGrid>
                    </MuiGrid>
                    <MuiGrid container>
                      <MuiGrid item md={6}>
                        Aggregate Limit:
                      </MuiGrid>
                      <MuiGrid item md={6}>
                        <strong className="details-value">
                          <CurrencyAmount
                            amount={quote.limit}
                            currency={quote.currency}
                            minimumFractionDigits={0}
                          />
                        </strong>
                      </MuiGrid>
                    </MuiGrid>
                    <MuiGrid container>
                      <MuiGrid item md={6}>
                        Deductible Amount:
                      </MuiGrid>
                      <MuiGrid item md={6}>
                        <strong className="details-value">
                          <CurrencyAmount
                            amount={quote.deductible}
                            currency={quote.currency}
                            minimumFractionDigits={0}
                          />
                        </strong>
                      </MuiGrid>
                    </MuiGrid>
                  </MuiGrid>
                  <MuiGrid
                    item
                    md={6}
                    className={classes.details}
                    style={{ paddingTop: 0, paddingBottom: 24 }}
                  >
                    <h3 className={classes.headings}>
                      AGENCY/AGENT INFORMATION
                    </h3>
                    <MuiGrid container>
                      <MuiGrid item md={4}>
                        From Agency:
                      </MuiGrid>
                      <MuiGrid className={REDACTED_FIELD} item md={6}>
                        <strong className="details-value">
                          {quote.agencyName}
                        </strong>
                      </MuiGrid>
                    </MuiGrid>
                    <MuiGrid container>
                      <MuiGrid item md={4}>
                        By Agent:
                      </MuiGrid>
                      <MuiGrid className={REDACTED_FIELD} item md={6}>
                        <strong className="details-value">
                          {quote.agentFirstName} {quote.agentLastName}
                        </strong>
                      </MuiGrid>
                    </MuiGrid>
                    <MuiGrid container>
                      <MuiGrid item md={4}>
                        Email:
                      </MuiGrid>
                      <MuiGrid className={REDACTED_FIELD} item md={6}>
                        <strong className="details-value">
                          {quote.agentEmail}
                        </strong>
                      </MuiGrid>
                    </MuiGrid>
                  </MuiGrid>
                </MuiGrid>
                <Divider
                  style={{
                    marginTop: '2.5rem',
                    marginBottom: '1.6666666667rem',
                  }}
                />
                <Box p={2} pb={5} textAlign="center">
                  <CbButton
                    styleName="cancel"
                    onClick={() => history.push('/customer/policies')}
                  >
                    Cancel
                  </CbButton>
                  <CbButton
                    loading={approving}
                    styleName="primary"
                    onClick={handleApproval}
                  >
                    Approve
                  </CbButton>
                </Box>
              </MuiGrid>
            </MuiGrid>
          </Box>
        </MuiCard>
      </section>
    </>
  );
});

const useStyles = makeStyles(({ palette }) => ({
  details: {
    '& .details-value': {
      color: palette.text.link,
      fontWeight: 600,
      textAlign: 'right',
    },
  },

  headings: {
    color: palette.primary.contrastText,
    marginTop: '2.5rem',
    marginBottom: 0,
    fontWeight: 400,
  },

  verticalDividingLine: {
    position: 'relative',

    '&:before': {
      content: "''",
      width: 1,
      position: 'absolute',
      height: '55%',
      top: '55%',
      right: 0,
      transform: 'translateY(-50%)',
      backgroundColor: palette.primary.border,
    },
  },
}));

const useGetDependencies = (quoteId: any, productType: any) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<{ quote: Record<string, any> }>({
    quote: {},
  });

  useEffect(() => {
    setLoading(true);
    if (productType === ProductTypes.p100) {
      fetchQuoteDetails(quoteId)
        .then((response) => {
          setData({
            quote: {
              ..._.pick(response.data, [
                'customerStatus',
                'quoteNumber',
                'totalPremium',
                'agencyName',
                'agentEmail',
                'agentFirstName',
                'agentLastName',
                'currency',
              ]),
              ..._.pick(response.data.initialRequestData, [
                'limit',
                'deductible',
                'isEndorsement',
              ]),
              ..._.pick(response.data.firmographicData, 'agencyBilled'),
            },
          });
          setLoading(false);
        })
        .catch(console.error.bind(console));
    } else if (productType === ProductTypes.p250) {
      fetchP250QuotePageDetails(quoteId)
        .then((response) => {
          setData({
            quote: {
              ..._.pick(response.data, [
                'customerStatus',
                'quoteNumber',
                'totalPremium',
                'agencyName',
                'agentEmail',
                'agentFirstName',
                'agentLastName',
                'currency',
              ]),
              ..._.pick(response.data.initial250RequestData, [
                'limit',
                'deductible',
              ]),
              ..._.pick(response.data.firmographicData, 'agencyBilled'),
            },
          });
          setLoading(false);
        })
        .catch(console.error.bind(console));
    }
  }, [productType, quoteId]);

  return {
    loading,
    data,
  };
};
